import React from 'react'
import Header from '../components/Header';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import { useSEO } from '../hooks/useSEO';

function CaseStudies() {
    useSEO({
        title: "Case Studies | Success Stories | Empower Web",
        description: "Explore our success stories and case studies showcasing how we've helped businesses achieve their digital goals with web development and marketing solutions.",
        canonical: "https://empowerweb.in/case-studies",
        ogTitle: "Case Studies | Success Stories & Results",
        ogDescription: "See how we've helped businesses achieve success with our digital solutions and strategies.",
        keywords: "case studies, success stories, project portfolio, client success, results, business growth"
    });
    const caseStudies = [
        {
            id: 1,
            title: "247DigiGrow: Scaling Amazon Sales with Reviews Management & Digital Marketing",
            client: "247DigiGrow",
            owner: "Adesh Sharma",
            industry: "E-Commerce",
            challenge: "Amazon seller struggled with low product visibility, inadequate review management, and no coordinated digital marketing strategy. Multiple product listings were underperforming with minimal customer engagement and reviews.",
            solution: "We developed a comprehensive platform for Amazon reviews management integrated with digital marketing campaigns. Built using Next.js and Node.js, the system automates review collection, monitoring, and response. Integrated with Amazon API for real-time data sync and built digital marketing automation for customer engagement.",
            results: [
                "Managing 500+ Amazon seller accounts successfully",
                "95% review response rate within 24 hours",
                "35% increase in product sales within 3 months",
                "400+ 5-star reviews generated through campaigns",
                "Average product ranking improved from page 3 to page 1"
            ],
            technologies: ["Next.js", "Node.js", "MongoDB", "Amazon API", "Marketing Automation"],
            testimonial: "The platform has transformed our Amazon selling business. Managing reviews and marketing is now automated and efficient. Our sales have increased significantly and customer satisfaction is at an all-time high.",
            testimonialAuthor: "Adesh Sharma, Owner - 247DigiGrow"
        },
        {
            id: 2,
            title: "Sankalp Ayurveda: Building a Modern E-Commerce Platform for Herbal Products",
            client: "Sankalp Ayurveda",
            owner: "Dinesh Thakur",
            industry: "Ayurvedic Medicine & Wellness",
            challenge: "Ayurvedic medicine company had limited online presence and struggled to reach customers beyond local markets. Manual processes for inventory, order management, and customer communication were time-consuming. No systematic lead generation or customer nurturing.",
            solution: "Developed a full-featured e-commerce platform using React.js with integrated automation and lead generation systems. Features include product catalog with detailed Ayurvedic descriptions, automated inventory management, integrated payment gateway, and automated email marketing for lead nurturing. CRM integration for customer relationship management.",
            results: [
                "50+ authentic Ayurvedic products listed with detailed descriptions",
                "2000+ monthly website visitors",
                "35% conversion rate (industry average: 8%)",
                "1000+ qualified leads generated monthly",
                "Inventory management automated, reducing manual work by 85%"
            ],
            technologies: ["React.js", "Automation Tools", "Lead Generation", "E-Commerce", "CRM Integration"],
            testimonial: "The new platform has been a game-changer for our Ayurvedic business. We can now reach customers nationally and manage everything from product catalog to customer communication efficiently.",
            testimonialAuthor: "Dinesh Thakur, Owner - Sankalp Ayurveda"
        },
        {
            id: 3,
            title: "SpeedRoutes: Courier Delivery Platform with Real-Time Tracking",
            client: "SpeedRoutes",
            owner: "Sandeep Singh",
            industry: "Logistics & Courier Services",
            challenge: "Courier company struggled with manual booking systems, lack of real-time tracking visibility, inefficient route management, and poor customer communication. Customers couldn't track packages and operations were not streamlined.",
            solution: "Built a comprehensive WordPress-based delivery management platform with custom plugins for real-time tracking, automated notifications, and route optimization. Integrated SMS and email notifications for customers, live GPS tracking dashboard for operations team, and automated invoice generation.",
            results: [
                "Manages 500+ daily deliveries efficiently",
                "98% on-time delivery rate",
                "Real-time tracking reduces customer inquiries by 80%",
                "20% faster package pickup and delivery workflow",
                "$100K annual savings in operational costs"
            ],
            technologies: ["WordPress", "Custom Plugins", "Tracking System", "SMS API", "Payment Integration"],
            testimonial: "The platform has revolutionized our courier operations. We can now handle 3x more deliveries with the same team. Customer satisfaction has improved dramatically with real-time tracking.",
            testimonialAuthor: "Sandeep Singh, Owner - SpeedRoutes"
        },
        {
            id: 4,
            title: "EduiTalent: HR & IT Consultancy Platform with Tech Expertise",
            client: "EduiTalent",
            owner: "Ritu",
            industry: "HR & IT Consultancy",
            challenge: "HR consultancy startup needed a professional platform to showcase IT consultancy expertise in emerging technologies like .NET, Java, Python, and AI. Manual consultancy process with no structured digital presence or product offerings.",
            solution: "Created a WordPress-based consultancy platform featuring expertise in multiple tech stacks (.NET, Java, AI, Python, and others). Built service offerings for IT product consultancy, HR solutions, and tech recruitment. Integrated CRM for lead management and project tracking systems.",
            results: [
                "20+ enterprise clients acquired in first year",
                "100+ successful placements and consultancy projects yearly",
                "Average project value increased by 45%",
                "95% client satisfaction rating",
                "3x increase in consultancy inquiries"
            ],
            technologies: ["WordPress", ".NET", "Java", "AI", "Python", "CRM Integration"],
            testimonial: "Working with this team has elevated our consultancy business. The platform showcases our expertise effectively and we're now attracting enterprise clients with complex tech requirements.",
            testimonialAuthor: "Ritu, Owner - EduiTalent"
        }
    ];

    return (
        <div>
            <Header />
            <Navbar />

            {/* Hero Section */}
            <section style={{background: 'linear-gradient(135deg, #0868a0 0%, #0d5a8f 100%)', padding: '80px 40px', color: 'white'}}>
                <div style={{maxWidth: '1200px', margin: '0 auto', textAlign: 'center'}}>
                    <h1 style={{fontSize: '3rem', marginBottom: '20px', fontWeight: 'bold'}}>Case Studies</h1>
                    <p style={{fontSize: '1.3rem', lineHeight: '1.8', maxWidth: '700px', margin: '0 auto'}}>
                        Real success stories from our clients. Discover how we've helped businesses transform their operations and achieve remarkable results.
                    </p>
                </div>
            </section>

            {/* Case Studies Section */}
            <section style={{padding: '80px 40px', background: '#fff'}}>
                <div style={{maxWidth: '1000px', margin: '0 auto'}}>
                    {caseStudies.map((study, index) => (
                        <div key={study.id} style={{
                            marginBottom: index !== caseStudies.length - 1 ? '60px' : '0',
                            paddingBottom: index !== caseStudies.length - 1 ? '60px' : '0',
                            borderBottom: index !== caseStudies.length - 1 ? '1px solid #e0e0e0' : 'none'
                        }}>
                            {/* Case Study Header */}
                            <div style={{marginBottom: '30px'}}>
                                <span style={{
                                    background: '#e3f2fd',
                                    color: '#0868a0',
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    fontSize: '0.9rem',
                                    display: 'inline-block',
                                    marginBottom: '15px'
                                }}>
                                    {study.industry}
                                </span>
                                <h2 style={{fontSize: '2rem', color: '#0868a0', marginBottom: '10px'}}>{study.title}</h2>
                                <p style={{color: '#666', fontSize: '1.1rem', marginBottom: '5px'}}>Client: <strong>{study.client}</strong></p>
                                <p style={{color: '#666', fontSize: '1rem'}}>Owner: <strong>{study.owner}</strong></p>
                            </div>

                            {/* Challenge Section */}
                            <div style={{marginBottom: '30px', background: '#f9f9f9', padding: '25px', borderRadius: '8px', borderLeft: '4px solid #ff6b6b'}}>
                                <h3 style={{color: '#ff6b6b', marginBottom: '12px', fontSize: '1.2rem'}}>🎯 Challenge</h3>
                                <p style={{color: '#666', lineHeight: '1.7', margin: 0}}>{study.challenge}</p>
                            </div>

                            {/* Solution Section */}
                            <div style={{marginBottom: '30px', background: '#f9f9f9', padding: '25px', borderRadius: '8px', borderLeft: '4px solid #4dabf7'}}>
                                <h3 style={{color: '#4dabf7', marginBottom: '12px', fontSize: '1.2rem'}}>💡 Solution</h3>
                                <p style={{color: '#666', lineHeight: '1.7', margin: 0}}>{study.solution}</p>
                            </div>

                            {/* Results Section */}
                            <div style={{marginBottom: '30px', background: '#f0f9ff', padding: '25px', borderRadius: '8px', borderLeft: '4px solid #51cf66'}}>
                                <h3 style={{color: '#51cf66', marginBottom: '15px', fontSize: '1.2rem'}}>📊 Results & Impact</h3>
                                <ul style={{margin: 0, paddingLeft: 0}}>
                                    {study.results.map((result, idx) => (
                                        <li key={idx} style={{
                                            color: '#666',
                                            marginBottom: '10px',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '12px'
                                        }}>
                                            <FiCheckCircle style={{color: '#51cf66', marginTop: '2px', flexShrink: 0}} />
                                            <span>{result}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Technologies Section */}
                            <div style={{marginBottom: '30px'}}>
                                <p style={{color: '#666', fontSize: '0.95rem', marginBottom: '10px'}}>Technologies Used:</p>
                                <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
                                    {study.technologies.map((tech, idx) => (
                                        <span key={idx} style={{
                                            background: '#0868a0',
                                            color: 'white',
                                            padding: '6px 14px',
                                            borderRadius: '4px',
                                            fontSize: '0.85rem'
                                        }}>
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Testimonial Section */}
                            <div style={{
                                background: '#fff9e6',
                                border: '2px solid #ffe066',
                                padding: '25px',
                                borderRadius: '8px'
                            }}>
                                <p style={{
                                    color: '#666',
                                    fontStyle: 'italic',
                                    fontSize: '1rem',
                                    lineHeight: '1.7',
                                    marginBottom: '15px',
                                    margin: '0 0 15px 0'
                                }}>
                                    "{study.testimonial}"
                                </p>
                                <p style={{color: '#0868a0', fontWeight: 'bold', margin: 0}}>
                                    — {study.testimonialAuthor}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA Section */}
            <section style={{background: '#f5f5f5', padding: '60px 40px'}}>
                <div style={{maxWidth: '1200px', margin: '0 auto', textAlign: 'center'}}>
                    <h2 style={{fontSize: '2.2rem', color: '#0868a0', marginBottom: '20px'}}>Ready to Create Your Success Story?</h2>
                    <p style={{fontSize: '1.1rem', color: '#666', marginBottom: '30px'}}>
                        Let's discuss how our proven expertise can help achieve your business goals.
                    </p>
                    <a href="/contact" style={{
                        background: '#0868a0',
                        color: 'white',
                        padding: '14px 32px',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        transition: 'all 0.3s ease'
                    }} onMouseEnter={(e) => {e.currentTarget.style.background = '#0d5a8f'}} 
                    onMouseLeave={(e) => {e.currentTarget.style.background = '#0868a0'}}>
                        Start a Case Study <FiArrowRight style={{marginLeft: '8px'}} />
                    </a>
                </div>
            </section>

            <Footer />
           
        </div>
    )
}

export default CaseStudies;
